import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Search } from 'lucide-react';
import { UI } from '../constants/designTokens';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { ErrorHandler } from '../utils/errorHandler';
import { formatDistanceToNow, format } from 'date-fns';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [clubChats, setClubChats] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/chat/conversations?filter=${filter}`);
      setConversations(response.data || []);
    } catch (error) {
      ErrorHandler.logError('MessagesPage - fetchConversations', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.get('/chat/conversations/unread-count');
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      ErrorHandler.logError('MessagesPage - fetchUnreadCount', error);
    }
  }, []);

  const fetchClubChats = useCallback(async () => {
    try {
      const response = await apiService.clubChats.list();
      setClubChats(response.data || []);
    } catch (error) {
      ErrorHandler.logError('MessagesPage - fetchClubChats', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchUnreadCount();
      fetchClubChats();
    }
  }, [user, fetchConversations, fetchUnreadCount, fetchClubChats]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else if (diffInDays < 7) {
      return format(date, 'EEE');
    } else {
      return format(date, 'MMM d');
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      conv.other_participant_name?.toLowerCase().includes(searchLower) ||
      conv.last_message_preview?.toLowerCase().includes(searchLower)
    );
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: UI.COLORS.BACKGROUND }}>
        <div className="text-center">
          <p style={{ color: UI.COLORS.TEXT_PRIMARY }}>Please log in to view messages</p>
          <Link to="/auth" className="mt-4 inline-block">
            <button className="px-4 py-2 rounded" style={{ backgroundColor: UI.COLORS.ACCENT, color: UI.COLORS.WHITE }}>
              Login
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: UI.COLORS.BACKGROUND, color: UI.COLORS.TEXT_PRIMARY }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: UI.COLORS.BACKGROUND, borderBottom: `1px solid ${UI.COLORS.BORDER}` }}>
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:opacity-70 transition-opacity"
              style={{ color: UI.COLORS.TEXT_PRIMARY }}
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold" style={{ fontFamily: UI.FONT_FAMILY.BOLD }}>
              Messages
            </h1>
          </div>
          <button
            className="p-2 hover:opacity-70 transition-opacity"
            style={{ color: UI.COLORS.TEXT_PRIMARY }}
          >
            <Edit size={24} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-4">
          <div className="relative">
            <Search 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2" 
              style={{ color: UI.COLORS.TEXT_TERTIARY }}
            />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg"
              style={{
                backgroundColor: UI.COLORS.FOREGROUND,
                color: UI.COLORS.TEXT_PRIMARY,
                border: `1px solid ${UI.COLORS.BORDER}`,
                fontFamily: UI.FONT_FAMILY.PRIMARY,
              }}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 px-4 pb-4">
          <button
            onClick={() => setFilter('all')}
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
            style={{
              backgroundColor: filter === 'all' ? UI.COLORS.ACCENT : 'transparent',
              color: filter === 'all' ? UI.COLORS.WHITE : UI.COLORS.TEXT_SECONDARY,
              fontFamily: UI.FONT_FAMILY.PRIMARY,
            }}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className="px-4 py-2 rounded-full text-sm font-medium transition-colors relative"
            style={{
              backgroundColor: filter === 'unread' ? UI.COLORS.ACCENT : 'transparent',
              color: filter === 'unread' ? UI.COLORS.WHITE : UI.COLORS.TEXT_SECONDARY,
              fontFamily: UI.FONT_FAMILY.PRIMARY,
            }}
          >
            Unread
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs"
                style={{ backgroundColor: UI.COLORS.ERROR, color: UI.COLORS.WHITE }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Club chats section */}
      {clubChats.length > 0 && (
        <div className="px-4 py-2" style={{ borderBottom: `1px solid ${UI.COLORS.BORDER}` }}>
          <h2 className="text-sm font-medium mb-2" style={{ color: UI.COLORS.TEXT_SECONDARY, fontFamily: UI.FONT_FAMILY.PRIMARY }}>
            Club chats
          </h2>
          {clubChats.map((chat) => (
            <Link
              key={chat.club_id}
              to={`/messages/club/${chat.club_id}`}
              className="block py-3 hover:opacity-80 transition-opacity"
              style={{ borderBottom: `1px solid ${UI.COLORS.BORDER_LIGHT}` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: chat.club_profile_picture ? 'transparent' : UI.COLORS.ACCENT,
                  }}
                >
                  {chat.club_profile_picture ? (
                    <img
                      src={chat.club_profile_picture}
                      alt={chat.club_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <span style={{ color: UI.COLORS.WHITE, fontFamily: UI.FONT_FAMILY.BOLD }}>
                      {chat.club_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium truncate" style={{ color: UI.COLORS.TEXT_PRIMARY, fontFamily: UI.FONT_FAMILY.PRIMARY }}>
                      {chat.club_name || 'Club'}
                    </h3>
                    <span className="text-xs ml-2 flex-shrink-0" style={{ color: UI.COLORS.TEXT_TERTIARY }}>
                      {formatTime(chat.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm truncate flex-1" style={{ color: UI.COLORS.TEXT_SECONDARY }}>
                      {chat.last_message_preview || 'No messages yet'}
                    </p>
                    {chat.unread_count > 0 && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                        style={{ backgroundColor: UI.COLORS.ACCENT, color: UI.COLORS.WHITE }}
                      >
                        {chat.unread_count > 9 ? '9+' : chat.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Conversations List */}
      <div className="pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div style={{ color: UI.COLORS.TEXT_SECONDARY }}>Loading conversations...</div>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <p style={{ color: UI.COLORS.TEXT_SECONDARY, marginBottom: UI.SPACING.MD }}>
              {searchQuery ? 'No conversations found' : filter === 'unread' ? 'No unread messages' : 'No conversations yet'}
            </p>
            {!searchQuery && filter === 'all' && (
              <p className="text-sm" style={{ color: UI.COLORS.TEXT_TERTIARY }}>
                Start a conversation with a runner or run club
              </p>
            )}
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <Link
              key={conversation.id}
              to={`/messages/${conversation.id}`}
              className="block px-4 py-3 hover:opacity-80 transition-opacity"
              style={{ borderBottom: `1px solid ${UI.COLORS.BORDER_LIGHT}` }}
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: conversation.other_participant_profile_picture 
                      ? 'transparent' 
                      : UI.COLORS.ACCENT,
                  }}
                >
                  {conversation.other_participant_profile_picture ? (
                    <img
                      src={conversation.other_participant_profile_picture}
                      alt={conversation.other_participant_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <span style={{ color: UI.COLORS.WHITE, fontFamily: UI.FONT_FAMILY.BOLD }}>
                      {conversation.other_participant_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3
                      className="font-medium truncate"
                      style={{
                        color: UI.COLORS.TEXT_PRIMARY,
                        fontFamily: UI.FONT_FAMILY.PRIMARY,
                      }}
                    >
                      {conversation.other_participant_name || 'Unknown User'}
                    </h3>
                    <span
                      className="text-xs ml-2 flex-shrink-0"
                      style={{ color: UI.COLORS.TEXT_TERTIARY }}
                    >
                      {formatTime(conversation.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p
                      className="text-sm truncate flex-1"
                      style={{ color: UI.COLORS.TEXT_SECONDARY }}
                    >
                      {conversation.last_message_preview || 'No messages yet'}
                    </p>
                    {conversation.unread_count > 0 && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                        style={{
                          backgroundColor: UI.COLORS.ACCENT,
                          color: UI.COLORS.WHITE,
                        }}
                      >
                        {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default MessagesPage;

